import CommonButton from "@/components/utils/CommonButton";
import PencilIcon from "@/components/utils/icons/PencilIcon";
import { useTenantMemberships } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import { LazoCompany } from "@/models/LazoCompany";
import { PORTAL_ROLES } from "@/types/LazoUserRoles";
import { prependHttps } from "@/utils/URIUtils";
import { Link, linkClasses, Typography, typographyClasses } from "@mui/material";
import { Box, styled } from "@mui/system";
import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import CompanyEditForm from "./CompanyEditForm";

export const InformationMainContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
}));

export const InformationHeader = styled(Box)(() => ({
  display: "flex",
  justifyContent: "space-between",
  flexDirection: "column",
  [`& .${typographyClasses.root}`]: {
    color: Colors.BLACK_151517,
    fontSize: "0.875rem",
    fontWeight: "600",
  },
}));

const InfoText = styled(Typography)(() => ({
  fontSize: "0.625rem !important",
  fontWeight: "400 !important",
}));

export const InformationHeaderInnerContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  paddingBottom: "0.5rem",
  [`& .${typographyClasses.root}`]: {
    color: Colors.BLACK_151517,
    fontSize: "0.875rem",
    fontWeight: "600",
  },
}));

export const InformationHeaderActionsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  ".action-edit-icon": {
    "&:hover": {
      cursor: "pointer",
    },
  },
  [theme.breakpoints.down("sm")]: {
    gap: "0.5rem",
  },
  [theme.breakpoints.up("sm")]: {
    gap: "1rem",
  },
}));

export const InformationContentContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "useFullHeight",
})<{
  useFullHeight?: boolean;
}>(({ useFullHeight = false, theme }) => ({
  display: "flex",
  flexDirection: "column",
  maxHeight: useFullHeight ? "100%" : "none",
  overflowY: useFullHeight ? "auto" : "visible",
  paddingTop: "0.5rem",
  [`& .${typographyClasses.root}`]: {
    fontWeight: "500",
    "&.label": {
      color: Colors.LIGHT_GRAY_8A8E93,
      [theme.breakpoints.down("sm")]: {
        fontSize: "0.75rem",
      },
      [theme.breakpoints.up("sm")]: {
        fontSize: "0.75rem",
      },
    },
    "&.data": {
      color: Colors.BLACK_151517,
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      [theme.breakpoints.down("sm")]: {
        fontSize: "0.875rem",
      },
      [theme.breakpoints.up("sm")]: {
        fontSize: "0.875rem",
      },
    },
  },
  [theme.breakpoints.down("sm")]: {
    gap: "1rem",
  },
  [theme.breakpoints.up("sm")]: {
    gap: "0.75rem",
  },
}));

export const InformationRowContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    gap: "0.75rem",
  },
  [theme.breakpoints.up("sm")]: {
    flexDirection: "row",
  },
}));

export const FieldContainer = styled(Box)({
  display: "flex",
  alignItems: "start",
  flexDirection: "column",
  textOverflow: "ellipsis",
  gap: "0.25rem",
  [`& .${linkClasses.root}`]: {
    color: Colors.BLUE_4763F4,
    ":hover": {
      cursor: "pointer",
    },
  },
});

const US_DOLLAR_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export type CompanyInformationProps = {
  company: LazoCompany;
  setCompany: Dispatch<SetStateAction<LazoCompany>>;
  title?: string;
  editable?: boolean;
  goToChat?: () => void;
};

export function CompanyInformationHeader({
  title,
  setFormOpen,
  hidePencil,
  goToChat,
  hideHeader,
  hideTitle,
}: {
  title?: string;
  setFormOpen: Dispatch<SetStateAction<boolean>>;
  hidePencil?: boolean;
  goToChat?: () => void;
  hideHeader?: boolean;
  hideTitle?: boolean;
}) {
  const { t } = useTranslation("settings");

  if (hideHeader) {
    return null;
  }

  // Show only the edit button when hideTitle is true
  if (hideTitle) {
    return (
      <InformationHeader>
        <InformationHeaderInnerContainer>
          <Box sx={{ flex: 1 }} />
          <InformationHeaderActionsContainer>
            {!hidePencil && <PencilIcon onClick={() => setFormOpen(true)} className={"action-edit-icon"} />}
          </InformationHeaderActionsContainer>
        </InformationHeaderInnerContainer>
      </InformationHeader>
    );
  }

  return (
    <InformationHeader>
      <InformationHeaderInnerContainer>
        <Typography>{title ? title : t("profile.companyInformation.title")}</Typography>
        <InformationHeaderActionsContainer>
          {goToChat && (
            <CommonButton className={"purple-border-variant"} onClick={goToChat} sx={{ height: "2rem" }}>
              {t("profile.companyInformation.contactKam")}
            </CommonButton>
          )}
          {!hidePencil && <PencilIcon onClick={() => setFormOpen(true)} className={"action-edit-icon"} />}
        </InformationHeaderActionsContainer>
      </InformationHeaderInnerContainer>
      <InformationHeaderInnerContainer>
        {goToChat && <InfoText>{t("profile.companyInformation.infoText")}</InfoText>}
      </InformationHeaderInnerContainer>
    </InformationHeader>
  );
}

function formatAsCurrency(val: string) {
  const num = parseFloat(val);
  if (isNaN(num)) {
    return val;
  }
  return `USD ${US_DOLLAR_FORMATTER.format(num)}`;
}

function CompanyInformation(props: CompanyInformationProps) {
  const { workspaceState } = useTenantMemberships();
  const { t } = useTranslation("settings");
  const shouldHide = !props.editable && !workspaceState.lazoMembership?.roles.some((role) => PORTAL_ROLES.includes(role.name));
  const [formOpen, setFormOpen] = useState<boolean>(false);

  return (
    <InformationMainContainer>
      <CompanyInformationHeader setFormOpen={setFormOpen} title={props.title} hidePencil={shouldHide} />
      <InformationContentContainer useFullHeight>
        <InformationRowContainer>
          <FieldContainer>
            <Typography className={"label"}>{t("profile.companyInformation.fields.companyName")}</Typography>
            <Typography className={"data"}>{props.company.companyName}</Typography>
          </FieldContainer>
          <FieldContainer>
            <Typography className={"label"}>{t("profile.companyInformation.fields.legalName")}</Typography>
            <Typography className={"data"}>{props.company.legalName}</Typography>
          </FieldContainer>
        </InformationRowContainer>
        <InformationRowContainer>
          <FieldContainer>
            <Typography className={"label"}>{t("profile.companyInformation.fields.country")}</Typography>
            <Typography className={"data"}>{props.company.country || "-"}</Typography>
          </FieldContainer>
          <FieldContainer>
            <Typography className={"label"}>{t("profile.companyInformation.fields.zipCode")}</Typography>
            <Typography className={"data"}>{props.company.address?.zipCode ? props.company.address?.zipCode : "-"}</Typography>
          </FieldContainer>
        </InformationRowContainer>
        <InformationRowContainer>
          <FieldContainer>
            <Typography className={"label"}>{t("profile.companyInformation.fields.city")}</Typography>
            <Typography className={"data"}>{props.company.address?.city ? props.company.address?.city : "-"}</Typography>
          </FieldContainer>
          <FieldContainer>
            <Typography className={"label"}>{t("profile.companyInformation.fields.state")}</Typography>
            <Typography className={"data"}>{props.company.address?.state ? props.company.address?.state : "-"}</Typography>
          </FieldContainer>
        </InformationRowContainer>
        <InformationRowContainer>
          <FieldContainer>
            <Typography className={"label"}>{t("profile.companyInformation.fields.website")}</Typography>
            <Typography className={"data"}>
              {props.company.url ? (
                <Link target={"_blank"} rel={"noopener"} href={prependHttps(props.company.url)}>
                  {t("profile.companyInformation.link")}
                </Link>
              ) : (
                "-"
              )}
            </Typography>
          </FieldContainer>
          <FieldContainer>
            <Typography className={"label"}>{t("profile.companyInformation.fields.address")}</Typography>
            <Typography className={"data"}>{props.company.address?.streetAddress ? props.company.address?.streetAddress : "-"}</Typography>
          </FieldContainer>
        </InformationRowContainer>
        <InformationRowContainer>
          <FieldContainer>
            <Typography className={"label"}>{t("profile.companyInformation.fields.yearFounded")}</Typography>
            <Typography className={"data"}>{props.company.yearFounded}</Typography>
          </FieldContainer>
          <FieldContainer>
            <Typography className={"label"}>{t("profile.companyInformation.fields.telephone")}</Typography>
            <Typography className={"data"}>{props.company.telephone}</Typography>
          </FieldContainer>
        </InformationRowContainer>
        <InformationRowContainer>
          <FieldContainer>
            <Typography className={"label"}>{t("profile.companyInformation.fields.numberOfEmployees")}</Typography>
            <Typography className={"data"}>{props.company.numberOfEmployees}</Typography>
          </FieldContainer>
          <FieldContainer>
            <Typography className={"label"}>{t("profile.companyInformation.fields.latestFinancialStage")}</Typography>
            <Typography className={"data"}>{props.company.startupStage}</Typography>
          </FieldContainer>
        </InformationRowContainer>
        <InformationRowContainer>
          <FieldContainer>
            <Typography className={"label"}>{t("profile.companyInformation.fields.ebitda")}</Typography>
            <Typography className={"data"}>{formatAsCurrency(props.company.companyContext?.ebitda || "-")}</Typography>
          </FieldContainer>
          <FieldContainer>
            <Typography className={"label"}>{t("profile.companyInformation.fields.keyInvestors")}</Typography>
            <Typography className={"data"}>{props.company.keyInvestors}</Typography>
          </FieldContainer>
        </InformationRowContainer>
        <InformationRowContainer>
          <FieldContainer>
            <Typography className={"label"}>{t("profile.companyInformation.fields.monthlyRevenue")}</Typography>
            <Typography className={"data"}>{formatAsCurrency(props.company.companyContext?.monthlyRevenue || "-")}</Typography>
          </FieldContainer>
          <FieldContainer>
            <Typography className={"label"}>{t("profile.companyInformation.fields.cashOnHand")}</Typography>
            <Typography className={"data"}>{formatAsCurrency(props.company.companyContext?.cashOnHand || "-")}</Typography>
          </FieldContainer>
        </InformationRowContainer>
        <InformationRowContainer>
          <FieldContainer>
            <Typography className={"label"}>{t("profile.companyInformation.fields.linkedin")}</Typography>
            <Typography className={"data"}>
              {props.company.companyContext?.linkedIn ? (
                <Link target={"_blank"} rel={"noopener"} href={prependHttps(props.company.companyContext.linkedIn)}>
                  {t("profile.companyInformation.link")}
                </Link>
              ) : (
                "-"
              )}
            </Typography>
          </FieldContainer>
          <FieldContainer>
            <Typography className={"label"}>{t("profile.companyInformation.fields.totalFundingReceived")}</Typography>
            <Typography className={"data"}>{formatAsCurrency(props.company.companyContext?.totalFundingReceived || "-")}</Typography>
          </FieldContainer>
        </InformationRowContainer>
        <InformationRowContainer>
          <FieldContainer>
            <Typography className={"label"}>{t("profile.companyInformation.fields.twitter")}</Typography>
            <Typography className={"data"}>
              {props.company.companyContext?.twitter ? (
                <Link target={"_blank"} rel={"noopener"} href={prependHttps(props.company.companyContext.twitter)}>
                  {t("profile.companyInformation.link")}
                </Link>
              ) : (
                "-"
              )}
            </Typography>
          </FieldContainer>
          <FieldContainer>
            <Typography className={"label"}>{t("profile.companyInformation.fields.instagram")}</Typography>
            <Typography className={"data"}>
              {props.company.companyContext?.instagram ? (
                <Link target={"_blank"} rel={"noopener"} href={prependHttps(props.company.companyContext.instagram)}>
                  {t("profile.companyInformation.link")}
                </Link>
              ) : (
                "-"
              )}
            </Typography>
          </FieldContainer>
        </InformationRowContainer>
        <FieldContainer>
          <Typography className={"label"}>{t("profile.companyInformation.fields.industry")}</Typography>
          <Typography className={"data"}>{props.company.companyContext?.industry || "-"}</Typography>
        </FieldContainer>
        <FieldContainer>
          <Typography className={"label"}>{t("profile.companyInformation.fields.shortCompanyDescription")}</Typography>
          <Typography className={"data"}>{props.company.companyContext?.companyDescription || "-"}</Typography>
        </FieldContainer>
      </InformationContentContainer>
      <CompanyEditForm setCompany={props.setCompany} company={props.company} setOpen={setFormOpen} open={formOpen} />
    </InformationMainContainer>
  );
}

export default CompanyInformation;
